/**
 * Application entry point handling authentication-aware routing
 *
 * @changeHistory
 * - 2026-10-01: Shared the complete broker workspace with admins (Codex)
 * - 2025-11-19: Lint fixes - Added file documentation and explicit return types (GitHub Copilot)
 * - 2026-02-02: Added admin diagnostics route (Antigravity)
 * - 2026-05-18: Added role-based gating for `/title-companies/import` route (Codex)
 * - 2026-07-23: Allowed admins to access the shared Team leadership workspace (Codex)
 * - 2026-08-13: Restored Transactions and Reporting routes for admins (Codex)
 */

import { lazy, Suspense, useEffect, type ReactElement } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@shared/stores/auth.store';
import { useAuthValidation } from '@shared/hooks/useAuthValidation';
import Layout from '@shared/components/Layout';
import { buildPageTitle } from '@shared/utils/page-title';
import { usePageViewTracking } from '@shared/analytics/usePageViewTracking';

// Lazy-loaded components for code splitting
// Each route is loaded only when navigated to, reducing initial bundle size
const LoginPage = lazy(() => import('@features/auth/LoginPage'));
const SubmissionPage = lazy(() => import('@features/submission/SubmissionPage'));
const EditCDAPage = lazy(() => import('@features/submission/EditCDAPage'));
const TransactionsPage = lazy(() => import('@features/transactions/TransactionsPage'));
const AgentDashboard = lazy(() => import('@features/dashboard/AgentDashboard'));
const BrokerDashboard = lazy(() => import('@features/reporting/BrokerDashboard'));
const TeamManagementPage = lazy(() => import('@features/team/TeamList'));
const AgentListPage = lazy(() => import('@features/agents/AgentList'));
const AgentDetailPage = lazy(() =>
  import('@features/agents/AgentDetail').then((module) => ({ default: module.AgentDetail }))
);
const AgentEditPage = lazy(() => import('./features/agents/AgentEditPage'));
const CompensationPlansPage = lazy(() =>
  import('@features/plans/PlanList').then((module) => ({ default: module.PlanList }))
);
const CompensationPlanForm = lazy(() =>
  import('@features/plans/PlanForm').then((module) => ({ default: module.PlanForm }))
);
const ListingsPage = lazy(() => import('@features/listings/ListingsPage'));
const ListingDetailsPage = lazy(() => import('@features/listings/ListingDetailsPage'));
const ListingsEditPage = lazy(() => import('@features/listings/ListingsEditPage'));
const EditCommercialListingPage = lazy(
  () => import('@features/listings/EditCommercialListingPage')
);
const CreateListingPage = lazy(() => import('@features/listings/CreateListingPage'));
const BlogPostsPage = lazy(() => import('@features/blog-posts/BlogPostsPage'));
const CreateBlogPostPage = lazy(() => import('@features/blog-posts/CreateBlogPostPage'));
const EditBlogPostPage = lazy(() => import('@features/blog-posts/EditBlogPostPage'));
const ProfileSettings = lazy(() => import('@features/profile/ProfileSettings'));
const AuditLogsPage = lazy(() => import('@features/audit/AuditLogsPage'));
const AdminDiagnostics = lazy(() => import('@features/admin/AdminDiagnostics'));
const TitleCompaniesPage = lazy(() => import('@features/title-companies/TitleCompaniesPage'));
const TitleCompaniesImportPage = lazy(
  () => import('@features/title-companies/TitleCompaniesImportPage')
);
const StatusPage = lazy(() => import('./pages/StatusPage'));

/**
 * Loading fallback component
 * Displayed while lazy-loaded routes are being fetched
 */
const LoadingFallback = (): ReactElement => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <div>Loading...</div>
  </div>
);

const App = (): ReactElement => {
  const { isAuthenticated, user } = useAuthStore();
  const { isValidating } = useAuthValidation();
  const { pathname } = useLocation();
  const canImportTitleCompanies =
    user?.role === 'broker' || user?.role === 'transaction_coordinator' || user?.role === 'admin';

  usePageViewTracking();

  const pageTitle = buildPageTitle({
    isAuthenticated,
    pathname,
    role: user?.role,
  });

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // Show loading screen while validating auth token
  if (isValidating) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/status" element={<StatusPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Agent routes */}
          {user?.role === 'agent' && (
            <>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<AgentDashboard />} />
              <Route path="/submit" element={<SubmissionPage />} />
              <Route path="/listings" element={<ListingsPage />} />
              <Route path="/listings/new" element={<CreateListingPage />} />
              <Route path="/listings/:id" element={<ListingDetailsPage />} />
              <Route path="/listings/:id/edit" element={<ListingsEditPage />} />
              <Route path="/listings/commercial/:id/edit" element={<EditCommercialListingPage />} />
              <Route path="/blog-posts" element={<BlogPostsPage />} />
              <Route path="/blog-posts/new" element={<CreateBlogPostPage />} />
              <Route path="/blog-posts/:slug/edit" element={<EditBlogPostPage />} />
              <Route path="/cda/:id/edit" element={<EditCDAPage />} />
            </>
          )}

          {/* Shared broker/admin routes, including personal Agent views */}
          {(user?.role === 'broker' || user?.role === 'admin') && (
            <>
              <Route path="/" element={<Navigate to="/transactions" replace />} />
              {/* Admin/Broker views */}
              <Route path="/team" element={<TeamManagementPage />} />
              <Route path="/agents" element={<AgentListPage />} />
              <Route path="/agents/:agentId" element={<AgentDetailPage />} />
              <Route path="/agents/:agentId/edit" element={<AgentEditPage />} />
              {/* Compensation Plans */}
              <Route path="/compensation-plans" element={<CompensationPlansPage />} />
              <Route path="/compensation-plans/new" element={<CompensationPlanForm />} />
              <Route path="/compensation-plans/:id/edit" element={<CompensationPlanForm />} />
              <Route path="/transactions" element={<TransactionsPage />} />
              <Route path="/listings" element={<ListingsPage />} />
              <Route path="/listings/new" element={<CreateListingPage />} />
              <Route path="/listings/:id" element={<ListingDetailsPage />} />
              <Route path="/listings/:id/edit" element={<ListingsEditPage />} />
              <Route path="/listings/commercial/:id/edit" element={<EditCommercialListingPage />} />
              <Route path="/blog-posts" element={<BlogPostsPage />} />
              <Route path="/blog-posts/new" element={<CreateBlogPostPage />} />
              <Route path="/blog-posts/:slug/edit" element={<EditBlogPostPage />} />
              <Route path="/cda/:id/edit" element={<EditCDAPage />} />
              <Route path="/reporting" element={<BrokerDashboard />} />
              <Route path="/plans" element={<CompensationPlansPage />} />
              <Route path="/audit-logs" element={<AuditLogsPage />} />
              {/* Agent views (dual-role) */}
              <Route path="/dashboard" element={<AgentDashboard />} />
              <Route path="/submit" element={<SubmissionPage />} />
            </>
          )}

          {/* Transaction Coordinator routes */}
          {user?.role === 'transaction_coordinator' && (
            <>
              <Route path="/" element={<Navigate to="/transactions" replace />} />
              <Route path="/submit" element={<SubmissionPage />} />
              <Route path="/transactions" element={<TransactionsPage />} />
              <Route path="/cda/:id/edit" element={<EditCDAPage />} />
            </>
          )}

          {/* Admin routes */}
          {user?.role === 'admin' && (
            <>
              <Route path="/admin" element={<Navigate to="/transactions" replace />} />
              <Route path="/admin/diagnostics" element={<AdminDiagnostics />} />
            </>
          )}

          {/* Shared routes - all authenticated users */}
          <Route path="/status" element={<StatusPage />} />
          <Route path="/profile" element={<ProfileSettings />} />
          <Route path="/title-companies" element={<TitleCompaniesPage />} />
          <Route
            path="/title-companies/import"
            element={
              canImportTitleCompanies ? (
                <TitleCompaniesImportPage />
              ) : (
                <Navigate to="/title-companies" replace />
              )
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
};

export default App;
